/**
 * SSO Callback page - handles redirect from OAuth providers (Google, Microsoft, etc.)
 *
 * Handles three scenarios:
 * 1. Authenticated: User already linked to SSO - log them in
 * 2. Link required: Account exists but not linked to SSO - prompt to link
 * 3. Registration required: New user - show organization choice form, then business name if creating new
 */

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQueryClient, type QueryClient } from '@tanstack/react-query';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Alert, AlertDescription, AlertTitle } from '../../components/ui/alert';
import { RadioGroup, RadioGroupItem } from '../../components/ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Loader2, Building2, Plus, Users } from 'lucide-react';
import { authApi, AuthApiError, SSOCallbackResponse } from '../../shared/auth/authApi';
import { AUTH_STORAGE_KEYS, redirectToCustomDomain } from '../../shared/auth';
import type { LoginResponse, MfaChallengeResponse, RegistrationOption, InvitationOption, DomainMatchOption } from '../../shared/auth/types';
import { MfaChallengeStep } from './components/MfaChallengeStep';
import { ApplicationReceivedScreen } from './ApplicationReceivedScreen';
import { API_BASE_URL, isCapacitor } from '../../shared/api/config';
import { getSSOItem, clearSSOState, removeSSOItem } from '../../shared/auth/ssoStorage';
import { closeInAppBrowser } from '../../shared/utils/capacitorDeepLinks';
import { externalAuthApi } from '../../external/services/externalAuthApi';
import {
  EXTERNAL_TOKEN_KEY,
  EXTERNAL_REFRESH_TOKEN_KEY,
  EXTERNAL_TENANT_SLUG_KEY,
} from '../../external/services/externalAuthStorage';
import { ConsentCheckboxes } from '../../shared/components/auth/ConsentCheckboxes';
import { getGenericErrorMessage } from "@/shared/utils/errors";
import { getExplicitLanguageChoice } from '../../i18n/useGeoLanguageDefault';
import { clearPersistedQueryCache } from '../../shared/offline/queryPersister';

/** Redirect to the stored return URL (from pre-login navigation) or home. */
function getPostLoginUrl(): string {
  const returnTo = getSSOItem('sso_return_to');
  removeSSOItem('sso_return_to');
  return returnTo || '/';
}

/**
 * Complete an SSO session establishment: purge the shared React Query cache —
 * both the in-memory `QueryClient` and its persisted IndexedDB snapshot — BEFORE
 * the hard reload, then navigate.
 *
 * The persisted cache is shared across every user on a browser profile (one
 * `PersistQueryClientProvider` at the app root) and is NOT keyed by user. Each
 * SSO completion point below is a login for a possibly-different user, so on a
 * shared/kiosk browser the incoming user would briefly render the previous
 * user's cached data on rehydrate unless we purge first. Every same-domain
 * redirect, the external-portal redirect, and the cross-domain bounces route
 * through here. Mirrors the AuthContext / ExternalAuthContext / TokenExchangePage
 * purge-on-auth-boundary fix.
 */
async function purgeCacheThenRedirect(queryClient: QueryClient, url: string): Promise<void> {
  await clearPersistedQueryCache(queryClient);
  window.location.href = url;
}

/**
 * Cross-domain (custom domain or local `*.panluma.local` subdomain) SSO must
 * complete the OAuth code exchange on the MAIN domain — the only host Google
 * has registered (cloud: `app.panluma.ai`; local: `localhost`). After the user
 * finishes authenticating there, every completion path must hand the session
 * back to the originating domain via a single-use code, otherwise the user is
 * left stranded on the main domain (the "app.panluma.local login redirects me
 * back to localhost" bug). Returns true if it navigated away (caller must stop),
 * false to fall back to a same-domain redirect.
 */
async function bounceToOriginatingDomain(
  queryClient: QueryClient,
  targetDomain: string,
  accessToken: string,
  refreshToken: string,
  user: unknown,
): Promise<boolean> {
  // Mobile stays inside its single bundled WebView origin — never cross-domain.
  if (isCapacitor) return false;
  try {
    const codeResponse = await fetch(`${API_BASE_URL}/api/v1/auth/cross-domain/create-code`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        target_domain: targetDomain,
        access_token: accessToken,
        refresh_token: refreshToken,
        user,
      }),
    });
    if (!codeResponse.ok) return false;
    const codeData = await codeResponse.json();
    const oneTimeCode = codeData.data?.code;
    if (!oneTimeCode) return false;

    // Local subdomains use http + their dev port; cloud custom domains use https.
    const isLocal = targetDomain.includes('localhost') || targetDomain.includes('.panluma.local');
    const protocol = isLocal ? 'http' : 'https';
    const port = isLocal && !targetDomain.includes(':') ? ':3003' : '';
    await purgeCacheThenRedirect(
      queryClient,
      `${protocol}://${targetDomain}${port}/auth/token-exchange?code=${oneTimeCode}`,
    );
    return true;
  } catch (err) {
    console.error('Cross-domain redirect failed, staying on current domain:', err);
    return false;
  }
}

type CallbackStatus = 'loading' | 'mfa_challenge' | 'link_required' | 'registration_required' | 'error';
type RegistrationStep = 'choice' | 'details' | 'invitation_consent';

/**
 * Centered card wrapper shared by every callback state.
 *
 * MUST stay at module scope. Declaring it inside `SSOCallbackPage` gave it a
 * fresh component identity on every render, so each keystroke in the
 * registration form remounted the whole subtree and the focused input lost
 * focus after every character (the "business name after reg loses focus" bug).
 */
function PageWrapper({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="max-w-[450px] w-full">
        <CardContent className="p-8 space-y-6">
          {children}
        </CardContent>
      </Card>
    </div>
  );
}

export function SSOCallbackPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation('auth');
  const { t: tCommon } = useTranslation('common');
  const queryClient = useQueryClient();

  const postLoginUrlRef = useRef<string>('/');
  const [status, setStatus] = useState<CallbackStatus>('loading');
  const [error, setError] = useState<string | null>(null);
  const [callbackData, setCallbackData] = useState<SSOCallbackResponse | null>(null);
  const [invitationReturnToken, setInvitationReturnToken] = useState<string | null>(null);
  const [mfaChallenge, setMfaChallenge] = useState<MfaChallengeResponse | null>(null);
  const [mfaEmail, setMfaEmail] = useState<string>('');

  // Link account state
  const [linkPassword, setLinkPassword] = useState('');
  const [linkError, setLinkError] = useState<string | null>(null);
  const [isLinking, setIsLinking] = useState(false);

  // Registration state
  const [registrationStep, setRegistrationStep] = useState<RegistrationStep>('choice');
  const [selectedOption, setSelectedOption] = useState<string>('create_new');
  const [selectedBusinessId, setSelectedBusinessId] = useState<string>('');
  const [businessName, setBusinessName] = useState('');
  // Apple Sign in only returns names on the very first sign-in per Apple ID,
  // and "Hide My Email" can also strip them. Keep these editable so the user
  // can fill in what the IdP didn't supply.
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [registerError, setRegisterError] = useState<string | null>(null);
  const [isRegistering, setIsRegistering] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [contentProcessingConsent, setContentProcessingConsent] = useState(false);
  const [pendingInvitationToken, setPendingInvitationToken] = useState<string | null>(null);
  const [pendingTargetDomain, setPendingTargetDomain] = useState<string | null>(null);
  // Set when the sysadmin tenant-approval gate held a new SSO tenant for review:
  // no tokens are issued, so we show the "application received" screen instead of
  // establishing a session (parity with the setup wizard's pending path).
  const [pendingApproval, setPendingApproval] = useState<{ email: string; companyName: string } | null>(null);
  // Originating domain for a cross-domain SSO flow (custom domain / *.panluma.local
  // subdomain). Persisted so the deferred completion paths (MFA, link, register)
  // can bounce the session back to it after auth finishes on the main domain.
  const [crossDomainTarget, setCrossDomainTarget] = useState<string | null>(null);

  // Prevent duplicate code exchange. Guard MUST survive a remount: React 18
  // StrictMode (dev) and route transitions can unmount and remount this page
  // before the in-flight code exchange resolves, which resets any per-instance
  // useRef. OAuth codes are single-use — Google returns invalid_grant on the
  // second exchange. We key the guard on the code itself in sessionStorage so
  // it survives across the mount/unmount/remount cycle.
  const codeExchangeAttempted = useRef(false);

  // Seed name inputs from the IdP payload as soon as it arrives.
  useEffect(() => {
    if (callbackData?.sso_info) {
      setFirstName((prev) => prev || callbackData.sso_info!.first_name || '');
      setLastName((prev) => prev || callbackData.sso_info!.last_name || '');
    }
  }, [callbackData]);

  useEffect(() => {
    const handleCallback = async () => {
      // Close the in-app browser if we're in a Capacitor app
      // This handles the case where OAuth was opened via Browser plugin
      if (isCapacitor) {
        closeInAppBrowser();
      }

      // Prevent duplicate code exchange — OAuth codes are single-use.
      // Per-instance ref handles in-render duplicates; sessionStorage key
      // handles unmount/remount cycles (StrictMode, route transitions).
      if (codeExchangeAttempted.current) {
        return;
      }
      const codeForGuard = searchParams.get('code');
      if (codeForGuard) {
        const guardKey = `__panluma_sso_code_exchanged:${codeForGuard}`;
        if (sessionStorage.getItem(guardKey)) {
          codeExchangeAttempted.current = true;
          return;
        }
        sessionStorage.setItem(guardKey, '1');
      }

      // Native-app SSO (currently iOS Sign in with Apple): the LoginPage /
      // RegisterPage have already exchanged the identity token and parked
      // the SSOCallbackResponse in sessionStorage. Skip the OAuth code path
      // entirely and route into the same downstream handling.
      if (searchParams.get('native') === '1') {
        codeExchangeAttempted.current = true;
        const raw = sessionStorage.getItem('__panluma_native_sso_response');
        sessionStorage.removeItem('__panluma_native_sso_response');
        if (!raw) {
          setError(t('sso.error'));
          setStatus('error');
          return;
        }
        try {
          const response = JSON.parse(raw) as SSOCallbackResponse;
          if (response.challenge_token) {
            setMfaChallenge(response as unknown as MfaChallengeResponse);
            setMfaEmail(response.sso_info?.email ?? '');
            setStatus('mfa_challenge');
            return;
          }
          if (response.status === 'authenticated' && response.access_token && response.refresh_token && response.user) {
            localStorage.setItem(AUTH_STORAGE_KEYS.accessToken, response.access_token);
            localStorage.setItem(AUTH_STORAGE_KEYS.refreshToken, response.refresh_token);
            localStorage.setItem(AUTH_STORAGE_KEYS.user, JSON.stringify(response.user));
            await purgeCacheThenRedirect(queryClient, '/');
            return;
          }
          if (response.status === 'link_required') {
            setCallbackData(response);
            setStatus('link_required');
            return;
          }
          if (response.status === 'registration_required') {
            setCallbackData(response);
            setStatus('registration_required');
            return;
          }
        } catch {
          setError(t('sso.error'));
          setStatus('error');
        }
        return;
      }

      const code = searchParams.get('code');
      const state = searchParams.get('state');
      const errorParam = searchParams.get('error');

      // Check for OAuth errors
      if (errorParam) {
        setError(t('sso.error'));
        setStatus('error');
        return;
      }

      // Validate required params
      if (!code) {
        setError('Missing authorization code');
        setStatus('error');
        return;
      }

      // Parse state to check for cross-domain flow
      // Format: "state_token" or "state_token|base64_encoded_payload"
      // Payload is either a plain domain string (legacy) or JSON object with domain + extra data
      let stateToken = state || '';
      let targetDomain: string | null = null;
      let statePayload: Record<string, string> | null = null;

      if (state && state.includes('|')) {
        const parts = state.split('|', 2);
        stateToken = parts[0];
        try {
          const standardBase64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
          const decoded = atob(standardBase64);
          // Try parsing as JSON (new format: {domain, invitation_token, ...})
          try {
            statePayload = JSON.parse(decoded);
            targetDomain = statePayload?.domain ?? null;
          } catch {
            // Legacy format: plain domain string
            targetDomain = decoded;
          }
        } catch {
          // Failed to decode payload - continue with same-domain flow
        }
      }

      // Validate state (CSRF protection).
      //
      // Same-domain states (no domain payload): the same-origin comparison
      // against the stored `sso_state` is the ONLY CSRF gate — a mismatch (or a
      // missing stored token) hard-fails locally and never reaches the backend.
      //
      // Cross-domain states (domain payload present): the backend is the
      // authoritative validator — because `targetDomain` is non-empty the
      // exchange below sends `source_domain`, which forces the server's
      // single-use, browser-BOUND state nonce check (`SSO_STATE_BINDING_COOKIE`
      // planted at /cross-domain/init). An attacker-minted state cannot be
      // consumed in the victim's browser (binding mismatch), so login-CSRF via
      // a crafted `domain` key still dies — server-side. A locally stored
      // `sso_state` that MISMATCHES such a callback is residue from a different
      // flow on this origin (e.g. a same-domain attempt that ended in
      // LOGIN_ON_CUSTOM_DOMAIN), not evidence of forgery: comparing against it
      // proves nothing, and hard-failing on it dead-ended every cross-domain
      // login after a same-domain attempt (the 2026-07-02..04 P0 SSO loop).
      // Drop the stale token and let the server nonce decide.
      const storedState = getSSOItem('sso_state');
      if (storedState !== null) {
        if (stateToken !== storedState) {
          if (!targetDomain) {
            setError(t('sso.invalidState'));
            setStatus('error');
            return;
          }
          removeSSOItem('sso_state');
        }
      } else if (!targetDomain) {
        // No stored state and not a cross-domain flow → the session that issued
        // the state is gone (expired or cleared).
        setError(t('sso.sessionExpired'));
        setStatus('error');
        return;
      }

      // Persist the originating domain so deferred completion paths (MFA, link,
      // register) can bounce the session back to it after auth finishes here.
      setCrossDomainTarget(targetDomain);

      const redirectUri = getSSOItem('sso_redirect_uri') || `${window.location.origin}/auth/sso/callback`;
      // Invitation data: prefer stored state (same-domain), fall back to state payload (cross-domain)
      const invitationToken = getSSOItem('sso_invitation_token') || statePayload?.invitation_token || null;
      const invitationTenantId = getSSOItem('sso_invitation_tenant_id') || statePayload?.invitation_tenant_id || null;
      const invitationTenantName = getSSOItem('sso_invitation_tenant_name') || statePayload?.invitation_tenant_name || null;

      // Mark as attempted before the API call to prevent re-entry
      codeExchangeAttempted.current = true;

      // External portal SSO: the OAuth state carries `portal_tenant_slug`. Route
      // the response to the external callback, which mints an EXTERNAL session
      // tenant-scoped to the portal — never the internal flow below.
      const portalTenantSlug = statePayload?.portal_tenant_slug;
      if (portalTenantSlug) {
        // The portal flow is always same-origin, so it must satisfy the CSRF
        // state check directly — never rely on the cross-domain bypass above
        // (a crafted state carrying a `domain` key would otherwise skip it and
        // log the victim into an attacker-supplied account / login-CSRF).
        if (!storedState || stateToken !== storedState) {
          setError(storedState === null ? t('sso.sessionExpired') : t('sso.invalidState'));
          setStatus('error');
          return;
        }
        try {
          const provider = getSSOItem('sso_provider') || 'google';
          const sessionResp = await externalAuthApi.ssoCallback(
            provider,
            portalTenantSlug,
            code,
            redirectUri,
          );
          clearSSOState();
          localStorage.setItem(EXTERNAL_TOKEN_KEY, sessionResp.access_token);
          localStorage.setItem(EXTERNAL_REFRESH_TOKEN_KEY, sessionResp.refresh_token);
          localStorage.setItem(EXTERNAL_TENANT_SLUG_KEY, portalTenantSlug);
          await purgeCacheThenRedirect(queryClient, `/external/${portalTenantSlug}`);
        } catch (err) {
          clearSSOState();
          // externalAuthApi uses axios, so surface the backend error code's
          // message (ACCOUNT_LOCKED / ACCOUNT_PENDING_APPROVAL / etc.).
          const detail = (err as { response?: { data?: { detail?: { message?: string } } } })
            .response?.data?.detail;
          setError(detail?.message || t('sso.error'));
          setStatus('error');
        }
        return;
      }

      try {
        // Exchange code for user info
        const provider = getSSOItem('sso_provider') || 'google';
        // Apple-only: the form-callback bounce forwards the first-sign-in
        // `user` JSON in the URL. Parse and pass it through so we can
        // capture the user's name (Apple returns it only this once).
        let userData: Record<string, unknown> | null = null;
        const userParam = searchParams.get('user');
        if (userParam) {
          try {
            userData = JSON.parse(userParam);
          } catch {
            // If parsing fails, proceed without — the user can still
            // authenticate but we'll have no name to seed.
          }
        }
        // Forward the raw OAuth `state` so the backend can validate its
        // server-issued, single-use nonce — the authoritative CSRF defense for
        // cross-domain flows, where the same-origin check above is structurally
        // skipped (the originating subdomain's `sso_state` is unreadable here).
        const response = await authApi.ssoCallback(provider, code, redirectUri, targetDomain, userData, state);

        // Read return URL before clearing SSO state
        postLoginUrlRef.current = getPostLoginUrl();

        // Clear SSO state
        clearSSOState();

        if (response.challenge_token) {
          // MFA gate: backend issued a challenge instead of tokens.
          setMfaChallenge(response as unknown as MfaChallengeResponse);
          setMfaEmail(response.sso_info?.email ?? '');
          setStatus('mfa_challenge');
          return;
        }

        if (response.status === 'authenticated') {
          // Check if user came from an invitation (JoinPage)
          if (invitationToken && response.user) {
            const userTenantId = response.user.tenant_id;
            if (invitationTenantId && userTenantId === invitationTenantId) {
              setError(t('join.alreadyInSameOrg', { tenantName: invitationTenantName || '' }));
            } else {
              setError(t('join.alreadyInDifferentOrg', { tenantName: invitationTenantName || '' }));
            }
            setInvitationReturnToken(invitationToken);
            setStatus('error');
            return;
          }

          // User authenticated - check if this is a cross-domain flow
          // Skip on Capacitor — mobile app doesn't do cross-domain redirects
          if (targetDomain && !isCapacitor && response.access_token && response.refresh_token && response.user) {
            // Cross-domain flow: hand the session back to the originating domain.
            if (await bounceToOriginatingDomain(
              queryClient, targetDomain, response.access_token, response.refresh_token, response.user,
            )) {
              return;
            }
            setError(t('sso.error'));
            setStatus('error');
            return;
          }

          // Same-domain flow: store tokens and redirect
          if (response.access_token && response.refresh_token && response.user) {
            localStorage.setItem(AUTH_STORAGE_KEYS.accessToken, response.access_token);
            localStorage.setItem(AUTH_STORAGE_KEYS.refreshToken, response.refresh_token);
            localStorage.setItem(AUTH_STORAGE_KEYS.user, JSON.stringify(response.user));

            // Check if user should be redirected to their custom domain
            // Skip on Capacitor — mobile app always stays in the WebView
            if (response.redirect_to_domain && !isCapacitor) {
              // Redirect to custom domain with tokens via cross-domain code exchange
              try {
                const codeResponse = await fetch(`${API_BASE_URL}/api/v1/auth/cross-domain/create-code`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({
                    target_domain: new URL(response.redirect_to_domain).host,
                    access_token: response.access_token,
                    refresh_token: response.refresh_token,
                    user: response.user,
                  }),
                });

                if (codeResponse.ok) {
                  const codeData = await codeResponse.json();
                  const oneTimeCode = codeData.data?.code;
                  if (oneTimeCode) {
                    await purgeCacheThenRedirect(
                      queryClient,
                      `${response.redirect_to_domain}/auth/token-exchange?code=${oneTimeCode}`,
                    );
                    return;
                  }
                }
              } catch (err) {
                console.error('Cross-domain redirect failed, staying on current domain:', err);
              }
            }

            await purgeCacheThenRedirect(queryClient, postLoginUrlRef.current);
          }
        } else if (response.status === 'link_required') {
          // If user came from an invitation, show error — they already have an account
          if (invitationToken) {
            if (invitationTenantId && response.email) {
              setError(t('join.alreadyInDifferentOrg', { tenantName: invitationTenantName || '' }));
            } else {
              setError(t('join.alreadyInDifferentOrg', { tenantName: invitationTenantName || '' }));
            }
            setInvitationReturnToken(invitationToken);
            setStatus('error');
            return;
          }
          // Cross-domain is handled on completion: handleLinkAccount bounces
          // back to crossDomainTarget after the account is linked.
          setCallbackData(response);
          setStatus('link_required');
        } else if (response.status === 'registration_required') {
          // If we have an invitation token from JoinPage, route through a consent
          // confirmation screen before completing registration (Apple 5.1.1(i) requires
          // affirmative consent at registration, not silent auto-complete).
          if (invitationToken && response.registration_ticket && response.sso_info) {
            setCallbackData(response);
            setPendingInvitationToken(invitationToken);
            setPendingTargetDomain(targetDomain);
            setRegistrationStep('invitation_consent');
            setStatus('registration_required');
            return;
          }

          // Cross-domain is handled on completion: handleCompleteRegistration
          // bounces back to crossDomainTarget after registration.
          setCallbackData(response);
          // If no options, skip to details step directly
          if (!response.registration_options || response.registration_options.length === 0) {
            setRegistrationStep('details');
          }
          setStatus('registration_required');
        }
      } catch (err) {
        // The flow is dead (the code is consumed either way) — drop its SSO
        // storage. A leftover `sso_state` here armed the stale-token mismatch
        // that blocked the NEXT (cross-domain) attempt in the 2026-07-02..04 P0.
        clearSSOState();
        if (err instanceof AuthApiError) {
          // Wrong-subdomain redirect: account lives on a different subdomain —
          // hop to the correct domain's login page (mirrors LoginPage behavior).
          const details = err.details || {};
          if (err.code === 'LOGIN_ON_CUSTOM_DOMAIN' && details.url) {
            // Send the user to their tenant's domain. On native this re-points
            // the app's API at that domain in-app (no cross-origin WebView escape).
            redirectToCustomDomain(details.url);
            return;
          }
          setError(err.message);
        } else {
          setError(t('sso.error'));
        }
        setStatus('error');
      }
    };

    handleCallback();
  }, [searchParams, t, queryClient]);

  const handleLinkAccount = async () => {
    // `link_required` is the only status that carries the full identity — the
    // registration path gets display-only fields plus a server-side ticket.
    const ssoInfo = callbackData?.sso_info;
    if (!ssoInfo || !('provider_id' in ssoInfo) || !callbackData?.user_id) return;

    setLinkError(null);
    setIsLinking(true);

    try {
      const response = await authApi.ssoLink(
        callbackData.user_id,
        linkPassword,
        ssoInfo
      );

      if ('challenge_token' in response && response.challenge_token) {
        setMfaChallenge(response as MfaChallengeResponse);
        setMfaEmail(ssoInfo.email ?? '');
        setStatus('mfa_challenge');
        return;
      }

      const loginResponse = response as LoginResponse;
      // Cross-domain SSO: return to the originating domain instead of finishing here.
      if (crossDomainTarget && await bounceToOriginatingDomain(
        queryClient, crossDomainTarget, loginResponse.access_token, loginResponse.refresh_token, loginResponse.user,
      )) {
        return;
      }
      localStorage.setItem(AUTH_STORAGE_KEYS.accessToken, loginResponse.access_token);
      localStorage.setItem(AUTH_STORAGE_KEYS.refreshToken, loginResponse.refresh_token);
      localStorage.setItem(AUTH_STORAGE_KEYS.user, JSON.stringify(loginResponse.user));
      await purgeCacheThenRedirect(queryClient, postLoginUrlRef.current);
    } catch (err) {
      if (err instanceof AuthApiError) {
        setLinkError(err.message);
      } else {
        setLinkError(getGenericErrorMessage());
      }
    } finally {
      setIsLinking(false);
    }
  };

  const handleCancelLink = () => {
    navigate('/login');
  };

  // Get selected option details
  const getSelectedOptionDetails = (): { option: RegistrationOption; type: 'invitation' | 'domain_match' } | null => {
    if (!callbackData?.registration_options || selectedOption === 'create_new') return null;

    const [type, tenantId] = selectedOption.split(':');
    const option = callbackData.registration_options.find(
      opt => opt.type === type && opt.tenant_id === tenantId
    );
    return option ? { option, type: type as 'invitation' | 'domain_match' } : null;
  };

  const handleChoiceSubmit = () => {
    if (selectedOption === 'create_new') {
      setRegistrationStep('details');
    } else {
      const details = getSelectedOptionDetails();
      if (details?.type === 'domain_match' && !selectedBusinessId) {
        setRegisterError(t('register.selectBusinessRequired'));
        return;
      }
      // Proceed to complete registration
      handleCompleteRegistration();
    }
  };

  const handleCompleteRegistration = async () => {
    if (!callbackData?.registration_ticket) return;

    const isJoining = selectedOption !== 'create_new';

    if (!isJoining && !businessName.trim()) {
      setRegisterError(t('validation.businessNameRequired'));
      return;
    }

    const trimmedFirstName = firstName.trim();
    const trimmedLastName = lastName.trim();
    if (!trimmedFirstName) {
      setRegisterError(t('validation.firstNameRequired'));
      return;
    }
    if (!trimmedLastName) {
      setRegisterError(t('validation.lastNameRequired'));
      return;
    }

    if (!termsAccepted || !contentProcessingConsent) {
      setRegisterError(t('consent.contentProcessingRequired'));
      return;
    }

    setRegisterError(null);
    setIsRegistering(true);

    try {
      let joinTenantId: string | undefined;
      let joinBusinessId: string | undefined;

      if (isJoining) {
        const details = getSelectedOptionDetails();
        if (details) {
          joinTenantId = details.option.tenant_id;
          if (details.type === 'invitation') {
            joinBusinessId = (details.option as InvitationOption).company_id;
          } else if (details.type === 'domain_match') {
            joinBusinessId = selectedBusinessId;
          }
        }
      }

      const response = await authApi.ssoCompleteRegistration(
        callbackData.registration_ticket,
        trimmedFirstName,
        trimmedLastName,
        isJoining ? undefined : businessName.trim(),
        joinTenantId,
        joinBusinessId,
        undefined,
        termsAccepted,
        contentProcessingConsent,
        getExplicitLanguageChoice(),
      );

      // Sysadmin tenant-approval gate: the new workspace is held for review and NO
      // tokens were issued. Show the "application received" screen instead of trying
      // to establish a session (mirrors the setup wizard's pending path). Only the
      // create-new-tenant mode can be held; joiner modes never return this flag.
      if (response.pending_approval) {
        setPendingApproval({
          email: response.user?.email ?? callbackData.sso_info?.email ?? '',
          companyName: businessName.trim(),
        });
        return;
      }

      // Cross-domain SSO: return to the originating domain instead of finishing here.
      if (crossDomainTarget && await bounceToOriginatingDomain(
        queryClient, crossDomainTarget, response.access_token, response.refresh_token, response.user,
      )) {
        return;
      }

      // Store tokens and redirect
      localStorage.setItem(AUTH_STORAGE_KEYS.accessToken, response.access_token);
      localStorage.setItem(AUTH_STORAGE_KEYS.refreshToken, response.refresh_token);
      localStorage.setItem(AUTH_STORAGE_KEYS.user, JSON.stringify(response.user));
      await purgeCacheThenRedirect(queryClient, postLoginUrlRef.current);
    } catch (err) {
      if (err instanceof AuthApiError) {
        setRegisterError(err.message);
      } else {
        setRegisterError(getGenericErrorMessage());
      }
    } finally {
      setIsRegistering(false);
    }
  };

  // MFA challenge: backend issued a challenge instead of tokens
  if (status === 'mfa_challenge' && mfaChallenge) {
    const handleMfaAuthenticated = async (response: LoginResponse) => {
      // Cross-domain SSO: return to the originating domain instead of finishing here.
      if (crossDomainTarget && await bounceToOriginatingDomain(
        queryClient, crossDomainTarget, response.access_token, response.refresh_token, response.user,
      )) {
        return;
      }
      localStorage.setItem(AUTH_STORAGE_KEYS.accessToken, response.access_token);
      localStorage.setItem(AUTH_STORAGE_KEYS.refreshToken, response.refresh_token);
      localStorage.setItem(AUTH_STORAGE_KEYS.user, JSON.stringify(response.user));
      await purgeCacheThenRedirect(queryClient, postLoginUrlRef.current);
    };
    return (
      <PageWrapper>
        <MfaChallengeStep
          challenge={mfaChallenge}
          email={mfaEmail}
          onAuthenticated={handleMfaAuthenticated}
          onBack={() => navigate('/login')}
        />
      </PageWrapper>
    );
  }

  // Loading state
  if (status === 'loading') {
    return (
      <PageWrapper>
        <div className="text-center space-y-4">
          <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
          <p className="text-muted-foreground">{t('sso.connecting')}</p>
        </div>
      </PageWrapper>
    );
  }

  // Error state
  if (status === 'error') {
    return (
      <PageWrapper>
        <div className="space-y-6">
          <Alert variant="destructive">
            <AlertTitle>{tCommon('app.error')}</AlertTitle>
            <AlertDescription>{error}</AlertDescription>
          </Alert>
          <div className="flex gap-4">
            {invitationReturnToken && (
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => navigate(`/join?token=${invitationReturnToken}`)}
              >
                {t('join.backToInvitation')}
              </Button>
            )}
            <Button
              className={invitationReturnToken ? 'flex-1' : 'w-full'}
              onClick={() => navigate('/login')}
            >
              {t('verification.goToLogin')}
            </Button>
          </div>
        </div>
      </PageWrapper>
    );
  }

  // Link account
  if (status === 'link_required' && callbackData) {
    const providerMap: Record<string, string> = { google: 'Google', microsoft: 'Microsoft' };
    const providerName = providerMap[callbackData.sso_info?.provider || ''] || callbackData.sso_info?.provider || 'SSO';

    return (
      <PageWrapper>
        <div className="space-y-6">
          <div className="space-y-2">
            <h2 className="text-2xl font-bold">{t('sso.linkAccountTitle')}</h2>
            <p className="text-sm text-muted-foreground">
              {t('sso.linkAccountDescription', {
                email: callbackData.email,
                provider: providerName,
              })}
            </p>
          </div>

          {linkError && (
            <Alert variant="destructive">
              <AlertDescription>{linkError}</AlertDescription>
            </Alert>
          )}

          {callbackData.has_password && (
            <div className="space-y-2">
              <Label htmlFor="linkPassword">{t('sso.enterPassword')}</Label>
              <Input
                id="linkPassword"
                type="password"
                value={linkPassword}
                onChange={(e) => setLinkPassword(e.target.value)}
                disabled={isLinking}
                autoFocus
              />
            </div>
          )}

          <div className="flex gap-4">
            <Button
              variant="outline"
              className="flex-1"
              onClick={handleCancelLink}
              disabled={isLinking}
            >
              {t('sso.cancel')}
            </Button>
            <Button
              className="flex-1"
              onClick={handleLinkAccount}
              disabled={isLinking || (callbackData.has_password && !linkPassword)}
            >
              {isLinking ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              {t('sso.linkAccount')}
            </Button>
          </div>
        </div>
      </PageWrapper>
    );
  }

  // Tenant-approval gate held the new SSO workspace for sysadmin review — no
  // session was established. Show the confirmation screen (shared with the setup
  // wizard) instead of the registration form.
  if (pendingApproval) {
    return (
      <ApplicationReceivedScreen
        email={pendingApproval.email}
        companyName={pendingApproval.companyName}
      />
    );
  }

  // Registration with options
  if (status === 'registration_required' && callbackData) {
    const hasOptions = callbackData.registration_options && callbackData.registration_options.length > 0;

    // Invitation consent confirmation (auto-completed previously; now requires affirmative consent)
    if (registrationStep === 'invitation_consent' && pendingInvitationToken && callbackData.registration_ticket && callbackData.sso_info) {
      const handleInvitationSubmit = async () => {
        if (!termsAccepted || !contentProcessingConsent) {
          setRegisterError(t('consent.contentProcessingRequired'));
          return;
        }
        setRegisterError(null);
        setIsRegistering(true);
        try {
          const regResponse = await authApi.ssoCompleteRegistration(
            callbackData.registration_ticket!,
            callbackData.sso_info!.first_name,
            callbackData.sso_info!.last_name,
            undefined,
            undefined,
            undefined,
            pendingInvitationToken,
            termsAccepted,
            contentProcessingConsent,
            getExplicitLanguageChoice(),
          );

          // Cross-domain SSO: return to the originating domain instead of
          // finishing (and leaving a session) here on the main domain.
          if (pendingTargetDomain && regResponse.access_token && regResponse.refresh_token && regResponse.user
            && await bounceToOriginatingDomain(
              queryClient, pendingTargetDomain, regResponse.access_token, regResponse.refresh_token, regResponse.user,
            )) {
            return;
          }
          localStorage.setItem(AUTH_STORAGE_KEYS.accessToken, regResponse.access_token);
          localStorage.setItem(AUTH_STORAGE_KEYS.refreshToken, regResponse.refresh_token);
          localStorage.setItem(AUTH_STORAGE_KEYS.user, JSON.stringify(regResponse.user));
          await purgeCacheThenRedirect(queryClient, postLoginUrlRef.current);
        } catch (err) {
          if (err instanceof AuthApiError) {
            setRegisterError(err.message);
          } else {
            setRegisterError(getGenericErrorMessage());
          }
        } finally {
          setIsRegistering(false);
        }
      };

      return (
        <PageWrapper>
          <div className="space-y-6">
            <div className="space-y-2">
              <h2 className="text-2xl font-bold">{t('sso.completeRegistration')}</h2>
              <p className="text-sm text-muted-foreground">
                {t('sso.completeRegistrationDescription')}
              </p>
              <p className="text-sm font-medium">
                {callbackData.sso_info.first_name} {callbackData.sso_info.last_name} ({callbackData.sso_info.email})
              </p>
            </div>

            {registerError && (
              <Alert variant="destructive">
                <AlertDescription>{registerError}</AlertDescription>
              </Alert>
            )}

            <ConsentCheckboxes
              termsAccepted={termsAccepted}
              contentProcessingConsent={contentProcessingConsent}
              onTermsChange={setTermsAccepted}
              onContentProcessingChange={setContentProcessingConsent}
              disabled={isRegistering}
            />

            <Button
              className="w-full"
              size="lg"
              onClick={handleInvitationSubmit}
              disabled={isRegistering || !termsAccepted || !contentProcessingConsent}
            >
              {isRegistering ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              {t('sso.completeRegistration')}
            </Button>
          </div>
        </PageWrapper>
      );
    }

    // Step 1: Organization choice (if options exist)
    if (registrationStep === 'choice' && hasOptions) {
      return (
        <PageWrapper>
          <div className="space-y-6">
            <div className="space-y-2">
              <h2 className="text-2xl font-bold">{t('register.organizationChoiceTitle')}</h2>
              <p className="text-sm text-muted-foreground">
                {t('register.organizationChoiceDescription')}
              </p>
              <p className="text-sm font-medium">{callbackData.sso_info?.email}</p>
            </div>

            {registerError && (
              <Alert variant="destructive">
                <AlertDescription>{registerError}</AlertDescription>
              </Alert>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="firstName-choice">{t('register.firstName')} <span className="text-destructive">*</span></Label>
                <Input
                  id="firstName-choice"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  disabled={isRegistering}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="lastName-choice">{t('register.lastName')} <span className="text-destructive">*</span></Label>
                <Input
                  id="lastName-choice"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  disabled={isRegistering}
                />
              </div>
            </div>

            <RadioGroup value={selectedOption} onValueChange={setSelectedOption} className="space-y-3">
              {/* Show join options */}
              {callbackData.registration_options!.map((option) => {
                const optionValue = `${option.type}:${option.tenant_id}`;
                const isSelected = selectedOption === optionValue;

                if (option.type === 'invitation') {
                  const invOption = option as InvitationOption;
                  return (
                    <div key={optionValue} className="relative">
                      <div
                        className={`flex items-start space-x-3 rounded-lg border p-4 cursor-pointer transition-colors ${
                          isSelected ? 'border-primary bg-primary/5' : 'hover:border-primary/50'
                        }`}
                        onClick={() => setSelectedOption(optionValue)}
                      >
                        <RadioGroupItem value={optionValue} id={optionValue} className="mt-1" />
                        <div className="flex-1">
                          <Label htmlFor={optionValue} className="flex items-center gap-2 cursor-pointer font-medium">
                            <Building2 className="h-4 w-4" />
                            {t('register.joinInvitation', { tenantName: invOption.tenant_name })}
                          </Label>
                          <p className="text-sm text-muted-foreground mt-1">
                            {t('register.joinBusinessAs', { businessName: invOption.company_name })}
                            {invOption.is_admin && ` ${t('register.joinAsAdmin')}`}
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                } else if (option.type === 'domain_match') {
                  const domainOption = option as DomainMatchOption;
                  return (
                    <div key={optionValue} className="relative">
                      <div
                        className={`flex items-start space-x-3 rounded-lg border p-4 cursor-pointer transition-colors ${
                          isSelected ? 'border-primary bg-primary/5' : 'hover:border-primary/50'
                        }`}
                        onClick={() => setSelectedOption(optionValue)}
                      >
                        <RadioGroupItem value={optionValue} id={optionValue} className="mt-1" />
                        <div className="flex-1">
                          <Label htmlFor={optionValue} className="flex items-center gap-2 cursor-pointer font-medium">
                            <Users className="h-4 w-4" />
                            {t('register.joinDomainMatch', { tenantName: domainOption.tenant_name })}
                          </Label>
                          <p className="text-sm text-muted-foreground mt-1">
                            {t('register.domainMatchDescription')}
                          </p>
                          {isSelected && (
                            <div className="mt-3">
                              <Label htmlFor="business-select" className="text-sm">
                                {t('register.selectBusiness')}
                              </Label>
                              <Select value={selectedBusinessId} onValueChange={setSelectedBusinessId}>
                                <SelectTrigger id="business-select" className="mt-1">
                                  <SelectValue placeholder={t('register.selectBusinessPlaceholder')} />
                                </SelectTrigger>
                                <SelectContent>
                                  {domainOption.companies.map((business) => (
                                    <SelectItem key={business.id} value={business.id}>
                                      {business.name}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                }
                return null;
              })}

              {/* Create new organization option */}
              <div
                className={`flex items-start space-x-3 rounded-lg border p-4 cursor-pointer transition-colors ${
                  selectedOption === 'create_new' ? 'border-primary bg-primary/5' : 'hover:border-primary/50'
                }`}
                onClick={() => setSelectedOption('create_new')}
              >
                <RadioGroupItem value="create_new" id="create_new" className="mt-1" />
                <div className="flex-1">
                  <Label htmlFor="create_new" className="flex items-center gap-2 cursor-pointer font-medium">
                    <Plus className="h-4 w-4" />
                    {t('register.createNewOrganization')}
                  </Label>
                  <p className="text-sm text-muted-foreground mt-1">
                    {t('register.createNewDescription')}
                  </p>
                </div>
              </div>
            </RadioGroup>

            {selectedOption !== 'create_new' && (
              <ConsentCheckboxes
                termsAccepted={termsAccepted}
                contentProcessingConsent={contentProcessingConsent}
                onTermsChange={setTermsAccepted}
                onContentProcessingChange={setContentProcessingConsent}
                disabled={isRegistering}
              />
            )}

            <Button
              className="w-full"
              size="lg"
              onClick={handleChoiceSubmit}
              disabled={
                isRegistering ||
                (selectedOption !== 'create_new' && (!termsAccepted || !contentProcessingConsent))
              }
            >
              {isRegistering ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              {selectedOption === 'create_new' ? t('register.continue') : t('sso.completeRegistration')}
            </Button>
          </div>
        </PageWrapper>
      );
    }

    // Step 2: Business name (only when creating new organization)
    return (
      <PageWrapper>
        <div className="space-y-6">
          <div className="space-y-2">
            <h2 className="text-2xl font-bold">{t('sso.completeRegistration')}</h2>
            <p className="text-sm text-muted-foreground">
              {t('sso.completeRegistrationDescription')}
            </p>
            <p className="text-sm font-medium">{callbackData.sso_info?.email}</p>
          </div>

          {registerError && (
            <Alert variant="destructive">
              <AlertDescription>{registerError}</AlertDescription>
            </Alert>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="firstName">{t('register.firstName')} <span className="text-destructive">*</span></Label>
              <Input
                id="firstName"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                disabled={isRegistering}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="lastName">{t('register.lastName')} <span className="text-destructive">*</span></Label>
              <Input
                id="lastName"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                disabled={isRegistering}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="businessName">{t('register.businessName')} <span className="text-destructive">*</span></Label>
            <Input
              id="businessName"
              value={businessName}
              onChange={(e) => setBusinessName(e.target.value)}
              disabled={isRegistering}
            />
          </div>

          <ConsentCheckboxes
            termsAccepted={termsAccepted}
            contentProcessingConsent={contentProcessingConsent}
            onTermsChange={setTermsAccepted}
            onContentProcessingChange={setContentProcessingConsent}
            disabled={isRegistering}
          />

          <div className="flex gap-4">
            {hasOptions && (
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => setRegistrationStep('choice')}
                disabled={isRegistering}
              >
                {t('register.back')}
              </Button>
            )}
            <Button
              className={hasOptions ? "flex-1" : "w-full"}
              size="lg"
              onClick={handleCompleteRegistration}
              disabled={isRegistering || !businessName.trim() || !termsAccepted || !contentProcessingConsent}
            >
              {isRegistering ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              {t('sso.completeRegistration')}
            </Button>
          </div>
        </div>
      </PageWrapper>
    );
  }

  return null;
}
